// ErrorBoundary.js
// Catches React render errors and shows a branded recovery screen
// instead of a white screen. Wraps the entire app AND individual pages.
//
// Usage:
//   <ErrorBoundary>
//     <SomePage />
//   </ErrorBoundary>
//
//   <ErrorBoundary page="Dashboard">
//     <DashboardPage />
//   </ErrorBoundary>

import { Component } from "react";
import PropTypes from 'prop-types';
import * as Sentry from "@sentry/react";

const CYAN    = "#22D3EE";
const BG      = "#061018";
const SURFACE = "#0A1720";
const BORDER  = "#22D3EE22";
const MUTED   = "#7A8894";
const FONT    = "'Inter', system-ui, sans-serif";

ErrorBoundary.propTypes = {
  children: PropTypes.node,
};

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
    // Report to Sentry with component stack context
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack },
      },
      tags: {
        page: this.props.page || "unknown",
        boundary: this.props.minimal ? "page" : "app",
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { page, minimal } = this.props;
    const isChunkError = this.state.error?.message?.includes("Loading chunk") ||
                         this.state.error?.name === "ChunkLoadError";

    // Chunk errors: just reload silently
    if (isChunkError) {
      window.location.reload();
      return null;
    }

    // Minimal mode — for per-page boundaries (smaller error card)
    if (minimal) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: 40, fontFamily: FONT,
          minHeight: 200,
        }}>
          <div style={{
            background: SURFACE, border: `1px solid ${BORDER}`,
            borderRadius: 12, padding: "24px 28px", maxWidth: 420,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
              {page ? `${page} failed to load` : "Something went wrong"}
            </div>
            <div style={{ fontSize: 13, color: "#B5B9BC", marginBottom: 20, lineHeight: 1.5 }}>
              An unexpected error occurred. Your data is safe.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: "8px 18px", borderRadius: 7, border: `1px solid ${BORDER}`,
                  background: "transparent", color: CYAN, fontSize: 13, fontWeight: 600,
                  cursor: "pointer", fontFamily: FONT,
                }}>
                Try again
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: "8px 18px", borderRadius: 7, border: "none",
                  background: CYAN, color: "#0A1720", fontSize: 13, fontWeight: 700,
                  cursor: "pointer", fontFamily: FONT,
                }}>
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Full-page mode — for the root app boundary
    return (
      <div style={{
        minHeight: "100vh", background: BG, display: "flex",
        alignItems: "center", justifyContent: "center",
        padding: 20, fontFamily: FONT,
      }}>
        <div style={{ width: "100%", maxWidth: 480, textAlign: "center" }}>

          {/* Logo */}
          <div style={{ marginBottom: 32 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 12,
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 10, padding: "10px 20px",
            }}>
              <span style={{ fontSize: 18, fontWeight: 900, color: CYAN, letterSpacing: "0.08em" }}>EXIQ</span>
              <div style={{ width: 1, height: 18, background: BORDER }} />
              <span style={{ fontSize: 11, color: "#B5B9BC", fontWeight: 600, letterSpacing: "0.04em" }}>
                EVENT MARKETING INTELLIGENCE
              </span>
            </div>
          </div>

          {/* Error card */}
          <div style={{
            background: SURFACE, border: `1px solid ${BORDER}`,
            borderRadius: 14, padding: "36px 32px",
          }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>⚠️</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
              Something went wrong
            </div>
            <div style={{ fontSize: 14, color: "#B5B9BC", lineHeight: 1.7, marginBottom: 28 }}>
              An unexpected error occurred{page ? ` on the ${page} page` : ""}.
              Your data is safe — this is a display error only.
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 24 }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: "11px 24px", borderRadius: 8,
                  border: `1px solid ${BORDER}`, background: "transparent",
                  color: CYAN, fontSize: 14, fontWeight: 600,
                  cursor: "pointer", fontFamily: FONT,
                }}>
                Try again
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: "11px 24px", borderRadius: 8, border: "none",
                  background: CYAN, color: "#0A1720", fontSize: 14, fontWeight: 700,
                  cursor: "pointer", fontFamily: FONT,
                }}>
                Reload page
              </button>
            </div>

            {/* Error details (collapsed) */}
            <details style={{ textAlign: "left" }}>
              <summary style={{
                fontSize: 12, color: "#B5B9BC", cursor: "pointer",
                userSelect: "none", listStyle: "none",
              }}>
                ▸ Show error details
              </summary>
              <div style={{
                marginTop: 12, background: "#0F2230",
                border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: "12px 14px", fontSize: 11,
                fontFamily: "monospace", color: "#22D3EEaa",
                wordBreak: "break-all", lineHeight: 1.6,
              }}>
                {this.state.error?.toString()}
              </div>
            </details>
          </div>

          <div style={{ marginTop: 16, fontSize: 12, color: "#535C62" }}>
            If this keeps happening, contact your administrator.
          </div>
        </div>
      </div>
    );
  }
}

// Convenience wrapper for page-level boundaries
export function PageErrorBoundary({ children, page }) {
  return (
    <ErrorBoundary page={page} minimal>
      {children}
    </ErrorBoundary>
  );
}
